import { formatterPercentage, formatterDate } from '@/utilities/format';

type PastData = {
  net_income_growth_5y?: number | null;
  net_income_growth_3y?: number | null;
  net_income_growth_1y?: number | null;
  earnings_per_share_growth_5y?: number | null;
  earnings_per_share_growth_3y?: number | null;
  earnings_per_share_growth_1y?: number | null;
  revenue_growth_5y?: number | null;
  revenue_growth_3y?: number | null;
  revenue_growth_1y?: number | null;
  return_on_equity?: number | null;
  net_income_margin?: number | null;
  last_earnings_update?: number | null;
};

type FutureData = {
  next_earnings_release?: number | null;
};

type IndustryAveragesData = {
  pastFiveYearGrowth?: number | null;
};

type InputParams = {
  pastData?: PastData;
  futureData?: FutureData;
  industryAveragesData?: IndustryAveragesData;
};

export const getFormattedKeyInfo = ({
  pastData,
  industryAveragesData,
  futureData,
}: InputParams) => {
  const earningsGrowthRate =
    pastData?.net_income_growth_5y ||
    pastData?.net_income_growth_3y ||
    pastData?.net_income_growth_1y;

  const epsGrowthRate =
    pastData?.earnings_per_share_growth_5y ||
    pastData?.earnings_per_share_growth_3y ||
    pastData?.earnings_per_share_growth_1y;

  const industryGrowthRate = industryAveragesData?.pastFiveYearGrowth;

  const revenueGrowthRate =
    pastData?.revenue_growth_5y ||
    pastData?.revenue_growth_3y ||
    pastData?.revenue_growth_1y;

  const returnOnEquity = pastData?.return_on_equity;
  const netMargin = pastData?.net_income_margin;

  const earningUpdateAt =
    futureData?.next_earnings_release || pastData?.last_earnings_update;

  const earningUpdateAtLabel = futureData?.next_earnings_release
    ? 'Next Earnings Update'
    : pastData?.last_earnings_update
      ? 'Last Earnings Update'
      : 'Earnings Update';

  return {
    earningsGrowthRate: formatterPercentage(earningsGrowthRate),
    epsGrowthRate: formatterPercentage(epsGrowthRate),
    industryGrowthRate: formatterPercentage(industryGrowthRate),
    revenueGrowthRate: formatterPercentage(revenueGrowthRate),
    returnOnEquity: formatterPercentage(returnOnEquity),
    netMargin: formatterPercentage(netMargin),
    earningUpdateAt: formatterDate(earningUpdateAt),
    earningUpdateAtLabel,
  };
};

export const getPastTerms = (industryName: string) => ({
  earningsGrowthRate:
    'Annualized earnings growth rate considering historical data over 5-year, 3-year, or 1-year periods, with emphasis on longer-term data. Calculated using the line of best fit.',
  epsGrowthRate:
    'Annualized EPS growth rate which considers 5-year, 3-year, or 1-year period. Calculated using the line of best fit.',
  revenueGrowthRate:
    'Annualized revenue growth rate which considers 5-year, 3-year, or 1-year period. Calculated using the line of best fit.',
  industryGrowthRate: `${industryName} industry’s annualized earnings growth rate considering historical data over 5-year, 3-year, or 1-year periods, with emphasis on longer-term data. Calculated using the line of best fit.`,
  returnOnEquity:
    "Measures how effectively a company's management has used shareholder funds to generate profits. Can sometimes be skewed by debt making the ROE look higher than it would be without the usage of debt. When ROE is n/a it implies company has negative equity.",
  netMargin:
    'Ratio of net profit (revenue less expenses, income, and taxes) to total revenue.  Indicates the percentage of profit earned per unit of revenue',
});
